import { useEffect } from 'react';

/**
 * Tiny per-route SEO updater. Sets <title>, <meta name="description">, the
 * canonical link, Open Graph + Twitter tags, and (optionally) injects a
 * per-route JSON-LD structured-data block — all without react-helmet. Restores
 * whatever was there when the component unmounts so tabs don't leak meta.
 *
 * Why no full helmet lib: SPA → Googlebot still indexes by rendering the JS,
 * so this works in practice. The bigger SEO win is index.html base meta +
 * sitemap + content quality, which we already set.
 */
export default function useSeo({ title, description, canonical, ogUrl, jsonLd }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, value) => {
      const tag = document.querySelector(selector);
      if (!tag) return [null, null];
      const prev = tag.getAttribute('content');
      if (value) tag.setAttribute('content', value);
      return [tag, prev];
    };

    const descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag ? descTag.getAttribute('content') : null;
    if (description && descTag) descTag.setAttribute('content', description);

    let canonTag = document.querySelector('link[rel="canonical"]');
    const prevCanon = canonTag ? canonTag.getAttribute('href') : null;
    if (canonical) {
      if (!canonTag) {
        canonTag = document.createElement('link');
        canonTag.setAttribute('rel', 'canonical');
        document.head.appendChild(canonTag);
      }
      canonTag.setAttribute('href', canonical);
    }

    // Keep social preview tags in sync with the active route.
    const [ogTitle, prevOgTitle] = setMeta('meta[property="og:title"]', title);
    const [ogDesc, prevOgDesc] = setMeta('meta[property="og:description"]', description);
    const [ogUrlTag, prevOgUrl] = setMeta('meta[property="og:url"]', ogUrl || canonical);
    const [twTitle, prevTwTitle] = setMeta('meta[name="twitter:title"]', title);
    const [twDesc, prevTwDesc] = setMeta('meta[name="twitter:description"]', description);

    // Per-route JSON-LD: inject a tagged script we fully control + remove.
    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement('script');
      ldScript.type = 'application/ld+json';
      ldScript.setAttribute('data-seo-route', '1');
      ldScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      document.title = prevTitle;
      if (descTag && prevDesc !== null) descTag.setAttribute('content', prevDesc);
      if (canonTag && prevCanon !== null) canonTag.setAttribute('href', prevCanon);
      if (ogTitle && prevOgTitle !== null) ogTitle.setAttribute('content', prevOgTitle);
      if (ogDesc && prevOgDesc !== null) ogDesc.setAttribute('content', prevOgDesc);
      if (ogUrlTag && prevOgUrl !== null) ogUrlTag.setAttribute('content', prevOgUrl);
      if (twTitle && prevTwTitle !== null) twTitle.setAttribute('content', prevTwTitle);
      if (twDesc && prevTwDesc !== null) twDesc.setAttribute('content', prevTwDesc);
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
    };
  }, [title, description, canonical, ogUrl, jsonLd]);
}
